import { Inject, Injectable } from '@angular/core';
import { UrlService } from '@x/ecommerce-shop/app/core/url/url.service';
import { IShopConfig, SHOP_CONFIG } from '@x/ecommerce-shop/config/shop.config';
import { IShopTaxonDetail, IShopTaxonItem } from '@x/ecommerce/shop-client';

export interface TaxonMenuItem {
  id: number;
  name: string;
  relation: 'ascendents' | 'siblings' | 'children' | 'descendents';
  order: number;
  route: string;
  hasChildren: boolean;
}

@Injectable()
export class TaxonMenuService {
  getPath(item: IShopTaxonItem): string {
    const depth = item.slug.split('/').length - 1;

    if (
      this.shopConfig.features.mainMenu.taxonTargetLevel.includes(depth) &&
      item.descendentProductCount !== 0
    ) {
      return this.urlService.taxonUrl(item.id, item.slug);
    }

    return this.urlService.productListingUrl(item.id, item.slug);
  }

  constructor(
    private urlService: UrlService,
    @Inject(SHOP_CONFIG) private shopConfig: IShopConfig,
  ) {}

  public buildMenu(taxon: IShopTaxonDetail) {
    if (taxon.hidden) return [];

    const parents = this.getMenuForRelation(taxon, 'ascendents');
    const children = this.getMenuForRelation(taxon, 'children')
      .sort(this.sortName)
      .sort(this.sortOrder);
    const siblings = [
      this.convertItemForMenu(taxon, 'siblings'),
      ...(children.length ? [] : this.getMenuForRelation(taxon, 'siblings')),
    ]
      .sort(this.sortName)
      .sort(this.sortOrder);

    return [...parents, ...siblings, ...children];
  }

  private getMenuForRelation(
    taxon: IShopTaxonDetail,
    relation: 'ascendents' | 'siblings' | 'children',
  ): TaxonMenuItem[] {
    return taxon[relation]
      .filter((item) => !item.hidden)
      .filter((item) => item.descendentProductCount > 0 || item.productCount > 0)
      .map((item) => this.convertItemForMenu(item, relation));
  }

  private convertItemForMenu(
    item: IShopTaxonItem,
    relation: 'ascendents' | 'siblings' | 'children',
  ): TaxonMenuItem {
    return {
      id: item.id,
      name: item.name,
      relation: relation,
      order: item.position ?? 0,
      route: this.getPath(item),
      hasChildren: item.descendentProductCount !== 0,
    };
  }

  private sortName(a: TaxonMenuItem, b: TaxonMenuItem): number {
    return a.name < b.name ? -1 : 1;
  }

  private sortOrder(a: TaxonMenuItem, b: TaxonMenuItem): number {
    return a.order - b.order;
  }
}
